// #tag: ADMIN PROFILE AND SECURITY AUDIT SCREEN
// #purpose: Allows administrators to view active credentials, inspect granted capabilities, review session activity trails, and update identity details.
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  ChevronLeft,
  ChevronRight,
  KeyRound,
  LogOut,
  Shield,
  UserCircle,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../../context/AuthProvider';
import {
  fetchAdminProfile,
  fetchAdminProfileActivity,
  fetchAdminProfilePermission,
  patchAdminProfile,
  type AdminProfileActivityRow,
  type AdminProfilePermissionRow,
  type AdminProfileResponse,
} from '../../api/admin';
import {
  AdminDetailCard,
  AdminEmptyState,
  AdminKpiStrip,
  AdminPageLayout,
  AdminSearchInput,
  AdminStatusBadge,
  AdminTablePanel,
} from '../../components/admin/AdminPageLayout';
import { LoadingState, ErrorState } from '../../components/states/StateViews';

type ProfileSelection =
  | { kind: 'identity' }
  | { kind: 'access' }
  | { kind: 'permission'; id: string; row?: AdminProfilePermissionRow }
  | { kind: 'activity'; id: string; row?: AdminProfileActivityRow }
  | null;

function actionLabel(action: string) {
  return action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function ProfileSummaryCard({
  title,
  subtitle,
  icon: Icon,
  badge,
  onSelect,
}: {
  title: string;
  subtitle: string;
  icon: LucideIcon;
  badge?: React.ReactNode;
  onSelect: () => void;
}) {
  return (
    <button type="button" className="wfx-admin-profile-card glass-surface" onClick={onSelect}>
      <div className="wfx-admin-media-card-icon"><Icon size={24} aria-hidden="true" /></div>
      <div className="wfx-admin-page-card-copy">
        <strong>{title}</strong>
        <span className="wfx-muted">{subtitle}</span>
      </div>
      {badge && <div className="wfx-admin-page-card-stats">{badge}</div>}
    </button>
  );
}

function PermissionCard({ row, onSelect }: { row: AdminProfilePermissionRow; onSelect: () => void }) {
  return (
    <button type="button" className="wfx-admin-profile-perm-card glass-surface" onClick={onSelect}>
      <div className="wfx-admin-result-card-head"><AdminStatusBadge status={row.status} /></div>
      <strong>{row.label}</strong>
      <span className="wfx-muted wfx-admin-subline"><KeyRound size={14} /> {row.group}</span>
      <code className="wfx-admin-code-chip">{row.permission}</code>
    </button>
  );
}

function ActivityCard({ row, onSelect }: { row: AdminProfileActivityRow; onSelect: () => void }) {
  return (
    <button type="button" className="wfx-admin-profile-activity-card glass-surface" onClick={onSelect}>
      <div className="wfx-admin-result-card-head"><AdminStatusBadge status={row.action} /></div>
      <strong>{actionLabel(row.action)} · {row.module}</strong>
      <span className="wfx-muted wfx-admin-subline">{row.details ?? row.resourceId ?? 'No details'}</span>
      <div className="wfx-admin-page-card-stats"><span>{new Date(row.createdAt).toLocaleString()}</span></div>
    </button>
  );
}

function ProfileDetailCard({
  selection,
  profile,
  onClose,
}: {
  selection: ProfileSelection;
  profile: AdminProfileResponse;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const update = useMutation({
    mutationFn: patchAdminProfile,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-profile'] });
      void qc.invalidateQueries({ queryKey: ['admin-me'] });
    },
  });

  const permissionId = selection?.kind === 'permission' ? selection.id : null;
  const activityId = selection?.kind === 'activity' ? selection.id : null;

  const permission = useQuery({
    queryKey: ['admin-profile-permission', permissionId],
    queryFn: () => fetchAdminProfilePermission(permissionId!),
    enabled: selection?.kind === 'permission',
    initialData: selection?.kind === 'permission' && selection.row
      ? { permission: selection.row }
      : undefined,
  });

  const activity = useQuery({
    queryKey: ['admin-profile-activity', activityId],
    queryFn: () => fetchAdminProfileActivity(activityId!),
    enabled: selection?.kind === 'activity',
    initialData: selection?.kind === 'activity' && selection.row
      ? { activity: selection.row }
      : undefined,
  });

  if (!selection) return null;

  const user = profile.user;

  if (selection.kind === 'identity') {
    return (
      <AdminDetailCard
        open
        onClose={onClose}
        ariaLabel="Identity details"
        breadcrumb="Profile"
        title={user.name}
        illustration="admin.module.users"
        subtitle={<><AdminStatusBadge status={user.status} />{profile.isSuperAdmin && <span className="chip-pill">Super Admin</span>}</>}
      >
        <div className="wfx-admin-form">
          <label className="wfx-admin-field">
            <span>Display name</span>
            <input
              defaultValue={user.name}
              onBlur={(e) => {
                const value = e.target.value.trim();
                if (value && value !== user.name) update.mutate({ name: value });
              }}
            />
          </label>
        </div>
        <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
          <div><dt>Email</dt><dd>{user.email ?? '—'}</dd></div>
          <div><dt>Phone</dt><dd>{user.phone ?? '—'}</dd></div>
          <div><dt>Member since</dt><dd>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}</dd></div>
          <div><dt>User ID</dt><dd>{user.id}</dd></div>
          <div><dt>Profile completeness</dt><dd>{profile.stats.profileCompleteness}%</dd></div>
        </dl>
      </AdminDetailCard>
    );
  }

  if (selection.kind === 'access') {
    return (
      <AdminDetailCard
        open
        onClose={onClose}
        ariaLabel="Access details"
        breadcrumb="Profile"
        title={profile.roleLabel}
        illustration="admin.module.users"
        subtitle={<span className="chip-pill">{user.role}</span>}
      >
        {profile.roleDescription && <p className="wfx-admin-detail-message">{profile.roleDescription}</p>}
        <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
          <div><dt>Permissions</dt><dd>{profile.stats.permissionCount}</dd></div>
          <div><dt>Groups</dt><dd>{profile.stats.groupCount}</dd></div>
          <div><dt>Tier</dt><dd>{profile.roleMeta?.tier ?? '—'}</dd></div>
          <div><dt>Rank</dt><dd>{profile.roleMeta?.rank ?? '—'}</dd></div>
        </dl>
        <div className="wfx-admin-profile-groups">
          {profile.permissionGroups.map((group) => (
            <section key={group.group} className="wfx-admin-profile-group glass-surface">
              <h3><Shield size={16} /> {group.group}</h3>
              <ul className="wfx-admin-note-list">
                {group.permissions.map((perm) => <li key={perm.id}><code>{perm.id}</code> — {perm.label}</li>)}
              </ul>
            </section>
          ))}
        </div>
      </AdminDetailCard>
    );
  }

  if (selection.kind === 'permission') {
    const row = permission.data?.permission;
    return (
      <AdminDetailCard
        open
        onClose={onClose}
        ariaLabel="Permission details"
        breadcrumb="Granted permission"
        title={row?.label ?? 'Permission'}
        illustration="admin.module.users"
        subtitle={row ? <><AdminStatusBadge status={row.status} /><span className="chip-pill">{row.group}</span></> : undefined}
      >
        {permission.isError && <ErrorState message="Could not load permission." onRetry={() => void permission.refetch()} />}
        {row && (
          <>
            <p className="wfx-admin-detail-message">This permission is granted to your current role ({profile.roleLabel}).</p>
            <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
              <div><dt>Permission key</dt><dd><code>{row.permission}</code></dd></div>
              <div><dt>Group</dt><dd>{row.group}</dd></div>
              <div><dt>Status</dt><dd>{row.status}</dd></div>
            </dl>
          </>
        )}
      </AdminDetailCard>
    );
  }

  const act = activity.data?.activity;
  return (
    <AdminDetailCard
      open
      onClose={onClose}
      ariaLabel="Activity details"
      breadcrumb="Recent activity"
      title={act ? `${actionLabel(act.action)} · ${act.module}` : 'Activity'}
      illustration="admin.module.audit"
      subtitle={act ? <AdminStatusBadge status={act.action} /> : undefined}
    >
      {activity.isError && <ErrorState message="Could not load activity." onRetry={() => void activity.refetch()} />}
      {act && (
        <>
          {act.details && <p className="wfx-admin-detail-message">{act.details}</p>}
          <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
            <div><dt>Module</dt><dd>{act.module}</dd></div>
            <div><dt>Resource</dt><dd>{act.resourceId ?? '—'}</dd></div>
            <div><dt>IP address</dt><dd>{act.ipAddress ?? '—'}</dd></div>
            <div><dt>Timestamp</dt><dd>{new Date(act.createdAt).toLocaleString()}</dd></div>
          </dl>
        </>
      )}
    </AdminDetailCard>
  );
}

export function AdminProfileScreen() {
  const { logout } = useAuth();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [selection, setSelection] = useState<ProfileSelection>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, groupFilter]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-profile', debouncedQuery, groupFilter, page],
    queryFn: () => fetchAdminProfile({
      q: debouncedQuery || undefined,
      group: groupFilter,
      page,
      limit: 12,
    }),
  });

  const rows = data?.data ?? [];
  const activity = data?.recentActivity ?? [];
  const groups = data?.groups ?? [];
  const pagination = data?.pagination;
  const user = data?.user;

  const kpiItems = useMemo(() => [
    { label: 'Role', value: data?.roleLabel ?? '—' },
    { label: 'Permissions', value: data?.stats.permissionCount ?? 0 },
    { label: 'Profile', value: `${data?.stats.profileCompleteness ?? 0}%` },
    { label: 'Activity', value: data?.stats.recentActivityCount ?? 0 },
  ], [data]);

  if (isLoading && !data) return <LoadingState label="Loading profile…" />;
  if (isError) return <ErrorState message="Could not load profile." onRetry={() => void refetch()} />;

  return (
    <>
      <AdminPageLayout
        breadcrumb="Admin / Profile"
        title="Profile"
        description="Your admin identity, granted permissions, and recent console activity."
        icon={UserCircle}
        illustration="admin.module.users"
        actions={(
          <button type="button" className="secondary-action compact" onClick={logout}>
            <LogOut size={16} aria-hidden="true" />
            Sign out
          </button>
        )}
      >
        <AdminKpiStrip items={kpiItems} />

        <div className="wfx-admin-blog-grid">
          <ProfileSummaryCard
            title={user?.name ?? 'Identity'}
            subtitle={user?.email ?? user?.phone ?? 'Update your display name'}
            icon={UserCircle}
            badge={<AdminStatusBadge status={user?.status ?? 'active'} />}
            onSelect={() => setSelection({ kind: 'identity' })}
          />
          <ProfileSummaryCard
            title={data?.roleLabel ?? 'Access'}
            subtitle={`${data?.stats.permissionCount ?? 0} permissions across ${data?.stats.groupCount ?? 0} groups`}
            icon={Shield}
            badge={data?.isSuperAdmin ? <span className="chip-pill">Super Admin</span> : undefined}
            onSelect={() => setSelection({ kind: 'access' })}
          />
          {activity.slice(0, 1).map((row) => (
            <ProfileSummaryCard
              key={row.id}
              title="Recent activity"
              subtitle={row.details ?? `${actionLabel(row.action)} in ${row.module}`}
              icon={Activity}
              badge={<span className="wfx-muted">{new Date(row.createdAt).toLocaleDateString()}</span>}
              onSelect={() => setSelection({ kind: 'activity', id: row.id, row })}
            />
          ))}
          {rows.slice(0, 3).map((row) => (
            <PermissionCard
              key={row.id}
              row={row}
              onSelect={() => setSelection({ kind: 'permission', id: row.id, row })}
            />
          ))}
        </div>

        {activity.length > 0 && (
          <AdminTablePanel title="Recent activity">
            <div className="wfx-admin-blog-grid wfx-admin-blog-grid--compact">
              {activity.map((row) => (
                <ActivityCard
                  key={row.id}
                  row={row}
                  onSelect={() => setSelection({ kind: 'activity', id: row.id, row })}
                />
              ))}
            </div>
          </AdminTablePanel>
        )}

        <AdminTablePanel
          title="Granted permissions"
          toolbar={(
            <div className="wfx-admin-toolbar">
              <AdminSearchInput value={query} onChange={setQuery} placeholder="Search permissions…" />
              <select className="wfx-admin-select" value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} aria-label="Filter by group">
                <option value="all">All groups</option>
                {groups.map((group) => <option key={group} value={group}>{group}</option>)}
              </select>
            </div>
          )}
          footer={
            pagination && pagination.pages > 1 ? (
              <div className="wfx-admin-pagination">
                <button type="button" className="secondary-action compact" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  <ChevronLeft size={16} aria-hidden="true" />
                  Previous
                </button>
                <span className="wfx-muted">
                  Page {pagination.page} of {pagination.pages} · {pagination.total} permissions
                </span>
                <button type="button" className="secondary-action compact" disabled={page >= pagination.pages} onClick={() => setPage((p) => p + 1)}>
                  Next
                  <ChevronRight size={16} aria-hidden="true" />
                </button>
              </div>
            ) : undefined
          }
        >
          {rows.length === 0 ? (
            <AdminEmptyState message="No permissions match your filters." />
          ) : (
            <table className="wfx-admin-table">
              <thead>
                <tr><th>Permission</th><th>Group</th><th>Key</th><th>Status</th></tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="wfx-admin-table-row-clickable"
                    onClick={() => setSelection({ kind: 'permission', id: row.id, row })}
                  >
                    <td><strong>{row.label}</strong></td>
                    <td>{row.group}</td>
                    <td><code>{row.permission}</code></td>
                    <td><AdminStatusBadge status={row.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </AdminTablePanel>
      </AdminPageLayout>

      {data && <ProfileDetailCard selection={selection} profile={data} onClose={() => setSelection(null)} />}
    </>
  );
}
